import { ProductSync, type ProductSyncProps } from "@bcc/components/products/product-sync";
import { ShopifyImportModal } from "@bcc/components/products/shopify-import-modal";
import { filterLocalisedProductConfigGrinds } from "@bcc/components/shopify-import/utils/filter-localised-product-config-grinds";
import { getVendorLocaleMetadata } from "@bcc/components/views/products-view.utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bcc/components/ui/alert-dialog";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { useTaxRatesContext } from "@bcc/contexts/tax-rates-context";
import {
  homeRegionForVendor,
  parseBagSizeToKg,
  parseShipToCountries,
  resolveDeDomesticWholesalePrice,
} from "@bcc/lib/products/cross-border-tax";
import { parsePrice } from "@bcc/lib/utils";
import { capitalize } from "@client-shared/lib/string";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import { useConfigState } from "@client-shared/providers/config";
import { useInstallationsState } from "@client-shared/providers/installations";
import { useProductsState } from "@client-shared/providers/products";
import { useVendorContext } from "@client-shared/providers/vendor";
import { setAllRemoteProductsToSalesforceProducts } from "@client-shared/utilities/productSelectModal";
import type { ResolveWholesalePrice } from "@client-shared/utilities/productSelectModal";
import type { FormProduct, Installation } from "@shared/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { setMapProductsStepTestId } from "./map-products-step.utils";
import { OnboardingStepper } from "./onboarding-stepper";
import { SkuImportStep } from "./sku-import-step";

export interface MapProductsStepProps {
  testId?: string;
  productSyncProps?: Partial<ProductSyncProps>;
  onBack?: () => void;
  onSkip?: () => void | Promise<void>;
  onComplete?: () => void | Promise<void>;
}

const getInstallationTimestamp = (installation: Installation): number => {
  const timestamp = installation.updatedAt ?? installation.createdAt;

  if (!timestamp) {
    return 0;
  }

  const value = new Date(timestamp).getTime();

  return Number.isNaN(value) ? 0 : value;
};

const getPreferredInstallation = (installationsMap: Map<string, Installation> | undefined): Installation | undefined => {
  const installations = Array.from(installationsMap?.values() ?? []);

  if (installations.length === 0) {
    return undefined;
  }

  return [...installations].sort((left, right) => {
    if (left.state !== right.state) {
      if (left.state === "ACTIVE") {
        return -1;
      }

      if (right.state === "ACTIVE") {
        return 1;
      }
    }

    const timestampDifference = getInstallationTimestamp(right) - getInstallationTimestamp(left);

    if (timestampDifference !== 0) {
      return timestampDifference;
    }

    return left.id.localeCompare(right.id);
  })[0];
};

export function MapProductsStep({ testId, productSyncProps, onBack, onSkip, onComplete }: MapProductsStepProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const [productsState, , productsUtils] = useProductsState();
  const [installationsState, , installationsMethods] = useInstallationsState();
  const [vendorState] = useVendorContext();
  const [configState] = useConfigState();
  const [authState] = useSFDCAuthState();
  const ratesByRegion = useTaxRatesContext();
  // DE-domestic products must send the Kaffeesteuer-excluded wholesale price
  // (netto + VAT), same rule as the SS onboarding mapper — kept here as an
  // injected callback since `client-shared` must not import bcc's tax module.
  const resolveWholesalePrice: ResolveWholesalePrice = useCallback(
    (product, variant) => {
      const homeRegion = homeRegionForVendor(vendorState.currentVendor?.countryCode, vendorState.currentVendor?.region);
      const isDeDomestic = homeRegion === "DE" && parseShipToCountries(product.ship_to).length <= 1;
      if (!isDeDomestic) return variant.wholesalePrice;
      return resolveDeDomesticWholesalePrice(
        parsePrice(variant.price),
        parsePrice(variant.wholesalePrice),
        parseBagSizeToKg(variant.bagSize),
        ratesByRegion
      );
    },
    [vendorState.currentVendor, ratesByRegion]
  );

  const activeInstallation = useMemo(() => {
    return productSyncProps?.installation ?? getPreferredInstallation(installationsState.installations);
  }, [installationsState.installations, productSyncProps?.installation]);

  useEffect(() => {
    if (
      productSyncProps?.installation ||
      !installationsState.initialized ||
      installationsState.loading ||
      installationsState.error ||
      installationsState.installations !== undefined ||
      !installationsMethods?.fetch
    ) {
      return;
    }

    installationsMethods.fetch();
  }, [
    installationsMethods,
    installationsState.error,
    installationsState.initialized,
    installationsState.installations,
    installationsState.loading,
    productSyncProps?.installation,
  ]);

  useEffect(() => {
    if (
      productSyncProps?.installation ||
      !installationsState.initialized ||
      installationsState.loading ||
      installationsState.installations?.size !== 0
    ) {
      return;
    }

    navigate(ROUTE_PATHS.ONBOARDING, { replace: true });
  }, [
    installationsState.initialized,
    installationsState.installations,
    installationsState.loading,
    navigate,
    productSyncProps?.installation,
  ]);

  useEffect(() => {
    if (!activeInstallation?.id) {
      return;
    }
    productsUtils?.handleInstallationId(activeInstallation.id);
  }, [activeInstallation?.id, productsUtils]);

  const localizedProductConfig = useMemo(
    () =>
      filterLocalisedProductConfigGrinds(
        configState.config.selectedLocalisedProductConfig ?? configState.config.defaultRegionLocalisedProductConfig,
        configState.config.productConfig
      ),
    [
      configState.config.defaultRegionLocalisedProductConfig,
      configState.config.selectedLocalisedProductConfig,
      configState.config.productConfig,
    ]
  );

  const importedVariantIds = useMemo(() => {
    return (
      productsState.remoteFormProducts
        ?.flatMap((product) => {
          return product.variants?.filter((variant) => variant.isImportedLocalVariant).map((variant) => variant.id);
        })
        .filter((variantId): variantId is string => Boolean(variantId)) ?? []
    );
  }, [productsState.remoteFormProducts]);

  const resolvedSyncProps = useMemo(() => {
    const resolvedVendor = productSyncProps?.vendor ?? vendorState.currentVendor;
    const resolvedInstallation = activeInstallation;

    if (!resolvedVendor || !resolvedInstallation) {
      return undefined;
    }

    return {
      mode: "embedded",
      vendor: resolvedVendor,
      installation: resolvedInstallation,
      localProducts: productSyncProps?.localProducts ?? productsState.localProducts ?? [],
      remoteProducts: productSyncProps?.remoteProducts ?? productsState.remoteProducts ?? [],
      connections: productSyncProps?.connections ?? productsState.connections ?? [],
      remoteFormProducts: productSyncProps?.remoteFormProducts ?? productsState.remoteFormProducts ?? [],
      importedVariantIds: productSyncProps?.importedVariantIds ?? importedVariantIds,
      localizedProductConfig: productSyncProps?.localizedProductConfig ?? localizedProductConfig,
      ...productSyncProps,
    } satisfies ProductSyncProps;
  }, [
    activeInstallation,
    importedVariantIds,
    localizedProductConfig,
    productSyncProps,
    productsState.connections,
    productsState.localProducts,
    productsState.remoteFormProducts,
    productsState.remoteProducts,
    vendorState.currentVendor,
  ]);

  const serviceLabel = useMemo(() => {
    const serviceId = resolvedSyncProps?.installation.serviceId;
    if (!serviceId) {
      return "";
    }
    // Prefer the shared platform branding labels (e.g. "WooCommerce") used elsewhere in the UI;
    // fall back to a capitalised id for platforms without a dedicated translation key.
    const labelKey = `views.productsTable.source.${serviceId}`;
    const label = t(labelKey);
    return typeof label === "string" && label !== labelKey ? label : capitalize(serviceId);
  }, [resolvedSyncProps?.installation.serviceId, t]);

  const localProductCount = resolvedSyncProps?.localProducts?.length ?? 0;
  const remoteProductCount = resolvedSyncProps?.remoteProducts?.length ?? 0;

  const mappedProductCount = useMemo(() => {
    const connectedSourceIds = new Set(
      (resolvedSyncProps?.connections ?? []).filter((c) => c.state === "ACTIVE").map((c) => c.sourceObjectId)
    );
    return (resolvedSyncProps?.localProducts ?? []).filter((product) => product.variants?.some((v) => connectedSourceIds.has(v.id))).length;
  }, [resolvedSyncProps?.connections, resolvedSyncProps?.localProducts]);

  const isProductsLoading = Object.values(productsState.loading).some(Boolean);

  // Use the raw value (before resolvedSyncProps applies `?? []` / the `...productSyncProps` spread)
  // so the "transformation lag" state can actually fire while derived products are still undefined.
  const rawRemoteFormProducts = productSyncProps?.remoteFormProducts ?? productsState.remoteFormProducts;

  const isTransformationLoading =
    !isProductsLoading && (resolvedSyncProps?.localProducts?.length ?? 0) > 0 && rawRemoteFormProducts === undefined;

  const isLoading = isProductsLoading || isTransformationLoading;

  const hasNoPimProducts = (resolvedSyncProps?.localProducts?.length ?? 0) === 0;
  const hasVendorProducts = (resolvedSyncProps?.remoteProducts?.length ?? 0) > 0;
  // The import modal is fed from `remoteFormProducts`, which is derived from `remoteProducts`.
  // Gate on the derived list being available so we never show the import CTA (and open an empty
  // modal) while transformation is still pending.
  const hasImportableProducts = (resolvedSyncProps?.remoteFormProducts?.length ?? 0) > 0;
  const shouldShowSkuImport = !isLoading && hasNoPimProducts && hasVendorProducts && hasImportableProducts;

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
      return;
    }
    navigate(ROUTE_PATHS.ONBOARDING);
  }, [navigate, onBack]);

  const handleImportComplete = useCallback(
    async (products: FormProduct[]) => {
      const handlePostProducts = productsUtils?.handlePostProducts;
      const sfdcAuthToken = authState.sfdcAuthToken;
      const userId = vendorState.currentVendor?.userId;
      const vendorRegion = vendorState.currentVendor?.region;
      const currencyIso = vendorState.currencyObject?.iso;
      const installationId = activeInstallation?.id;

      if (!handlePostProducts || !sfdcAuthToken || !userId || !vendorRegion || !currencyIso || !installationId) {
        throw new Error("Unable to submit import: missing required authentication or configuration. Please refresh and try again.");
      }

      const { region: resolvedRegion, locales: regionLocales } = getVendorLocaleMetadata({
        region: vendorRegion,
        countryCode: vendorState.currentVendor?.countryCode,
      });

      const mappedProducts = await setAllRemoteProductsToSalesforceProducts(
        products,
        userId,
        currencyIso,
        resolvedRegion,
        installationId,
        regionLocales,
        resolveWholesalePrice
      );

      await handlePostProducts("salesforce", sfdcAuthToken, mappedProducts);
    },
    [
      productsUtils,
      authState.sfdcAuthToken,
      vendorState.currentVendor,
      vendorState.currencyObject,
      activeInstallation,
      resolveWholesalePrice,
    ]
  );

  const handleComplete = useCallback(async () => {
    setIsCompleting(true);
    try {
      if (onComplete) {
        await onComplete();
      } else {
        navigate(ROUTE_PATHS.ONBOARDING_SUCCESS);
      }
    } finally {
      setIsCompleting(false);
    }
  }, [navigate, onComplete]);

  const handleSkip = useCallback(async () => {
    setShowSkipDialog(false);
    setIsSkipping(true);
    try {
      if (onSkip) {
        await onSkip();
      } else {
        navigate(ROUTE_PATHS.ONBOARDING_SUCCESS);
      }
    } finally {
      setIsSkipping(false);
    }
  }, [navigate, onSkip]);

  const handleSyncComplete = useCallback(async () => {
    await handleComplete();
  }, [handleComplete]);

  if (shouldShowSkuImport) {
    return (
      <>
        <SkuImportStep testId={testId} platform={serviceLabel} onImport={() => setShowImportModal(true)} onBack={handleBack} />
        <ShopifyImportModal
          open={showImportModal}
          onOpenChange={setShowImportModal}
          products={resolvedSyncProps?.remoteFormProducts ?? []}
          isLoading={isLoading}
          onImportComplete={handleImportComplete}
          onImportFinished={handleComplete}
          productConfig={configState.config.productConfig}
          localisedProductConfig={localizedProductConfig}
          serviceId={activeInstallation?.serviceId}
          regionLargeBagSize={configState.config.regionLargeBagSize}
          roasterNoteMaxLength={configState.config.roasterNoteMaxLength}
          productDescriptionMaxLength={configState.config.productDescriptionMaxLength}
          titleMaxLength={configState.config.titleMaxLength}
          shortTermMinWeeklyInventory={configState.config.inventoryConfig?.shortTermMinWeeklyInventory}
          shortTermMaxWeeklyInventory={configState.config.inventoryConfig?.shortTermMaxWeeklyInventory}
          longTermMinWeeklyInventory={configState.config.inventoryConfig?.longTermMinWeeklyInventory}
          longTermMaxWeeklyInventory={configState.config.inventoryConfig?.longTermMaxWeeklyInventory}
        />
      </>
    );
  }

  return (
    <div data-testid={testId ?? setMapProductsStepTestId("container")} className="flex flex-col gap-8">
      <OnboardingStepper currentStep={2} />

      <div
        data-testid={setMapProductsStepTestId("layout-grid")}
        className="grid gap-6 xl:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] xl:gap-8"
      >
        <div data-testid={setMapProductsStepTestId("summary-rail")} className="flex flex-col gap-4">
          <div>
            <h2 data-testid={setMapProductsStepTestId("heading")} className="mb-2 text-xl font-semibold text-text-primary">
              {t("onboarding.mapProducts.title")}
            </h2>
            <p className="text-sm text-text-muted">
              {t("onboarding.mapProducts.description", {
                platform: serviceLabel,
              })}
            </p>
          </div>
        </div>

        <Card data-testid={setMapProductsStepTestId("sync-panel")} className="min-w-0 border-border-subtle bg-muted/20 shadow-sm">
          <CardContent className="min-w-0 space-y-4 p-6">
            <div className="space-y-2">
              <p className="text-sm text-text-muted">
                {t("onboarding.mapProducts.loadingProducts", {
                  defaultValue: "Map local products to your connected platform.",
                })}
              </p>
              <p data-testid={setMapProductsStepTestId("mapped-count")} className="text-sm text-text-muted">
                {t("onboarding.mapProducts.mappedCount", {
                  mapped: mappedProductCount,
                  total: localProductCount,
                  defaultValue: "{{mapped}} of {{total}} products mapped",
                })}
              </p>
              <p className="text-xs text-text-muted">
                {t("onboarding.mapProducts.table.products", {
                  defaultValue: "Products",
                })}
                : {serviceLabel} {remoteProductCount} / Beanz {localProductCount}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
              <Button
                data-testid={setMapProductsStepTestId("back-button")}
                variant="outline"
                onClick={handleBack}
                disabled={isCompleting || isSkipping}
              >
                {t("common.back")}
              </Button>
              <Button
                data-testid={setMapProductsStepTestId("skip-button")}
                variant="ghost"
                onClick={() => setShowSkipDialog(true)}
                disabled={isCompleting || isSkipping}
              >
                {t("onboarding.mapProducts.skipForNow")}
              </Button>
            </div>

            {resolvedSyncProps ? (
              <ProductSync {...resolvedSyncProps} mode="embedded" isLoading={isLoading} onSubmitSuccess={handleSyncComplete} />
            ) : installationsState.error ? (
              <p className="text-sm text-destructive">
                {t("common.errorRetry", {
                  defaultValue: "Something went wrong. Please refresh and try again.",
                })}
              </p>
            ) : (
              <p className="text-sm text-text-muted">{t("common.loading", { defaultValue: "Loading..." })}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("onboarding.mapProducts.skipDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("onboarding.mapProducts.skipDialog.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid={setMapProductsStepTestId("skip-anyway-button")} onClick={handleSkip} disabled={isSkipping}>
              {t("onboarding.mapProducts.skipDialog.skipAnyway")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
